import React, { useState, useEffect, useMemo } from 'react'
import { Heading, Text, Alert, Box, Stack, ResponsiveStack, Loading, Button, Badge, Tag, FormControl, Input, EmptyBox, SimpleModal, SimpleGrid, SearchableSelect, Icon, ColouredTag, DateChooser, ImageLogo, Wrap, WrapItem, ReadOnlyCopyInput } from '@/components/lib'
import ConfirmButton from '../../includes/ConfirmButton'
import { FaTimes, FaBox, FaLightbulb, FaSyncAlt } from 'react-icons/fa'
import { InsightCarousel } from '../../includes/ParticipantInsightCards'

import ParticipantMessenger from '../../includes/ParticipantMessenger'
import ParticipantNotes from '../../includes/ParticipantNotes'
import PhoneInput from '../../includes/PhoneInput'
import QrCode from '../../includes/QrCode'
import api from '../../../api'
import useStore from '../../../store'
import utils from '../../../utils/utils'

import FitbitImage from '../../../images/fitbit.png'
import GarminImage from '../../../images/garmin.png'

function InsightsBar ({ trialId, participantId }) {
  const { getTrialUsers, trial, user } = useStore()
  const trialUsers = useMemo(() => getTrialUsers(trial?._id), [trial?._id, getTrialUsers])
  const [insightsData, setInsightsData] = useState(undefined)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const canRefresh = utils.hasPermission(user, 'root') // Root only for now while testing: will become automatic only in future.

  useEffect(() => {
    if (!trialId || !participantId) return
    api.insights.getForParticipant(trialId, participantId, (d) => {
      setInsightsData(d.insights)
      setLoading(false)
    }, () => setLoading(false))
  }, [trialId, participantId])

  function refresh () {
    setRefreshing(true)
    api.insights.refreshForParticipant(trialId, participantId, (d) => {
      setInsightsData(d.insights)
      setRefreshing(false)
    }, () => setRefreshing(false))
  }

  if (loading) return null

  const items = insightsData?.items || []
  if (items.length === 0 && !canRefresh) return null

  return (
    <Box borderWidth='1px' rounded='md' bg='bg.surface' p={3}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
        <Stack direction='row' alignItems='center' gap={2}>
          <Icon as={FaLightbulb} fontSize='sm' color='yellow.400' />
          <Text fontSize='sm' fontWeight='semibold'>Insights</Text>
          <Badge colorPalette='yellow' variant='subtle'>Experimental</Badge>
        </Stack>
        {canRefresh &&
          <Button size='xs' variant='ghost' onClick={refresh} loading={refreshing}>
            <Icon as={FaSyncAlt} />
          </Button>}
      </Stack>
      <InsightCarousel
        insights={items}
        trialId={trialId}
        participantId={participantId}
        trialUsers={trialUsers}
      />
    </Box>
  )
}

const SERVICES = {
  fitbit: { name: 'Fitbit', image: FitbitImage },
  garmin: { name: 'Garmin', image: GarminImage }
}

export default function Information ({ trialId, participantId, participant, setParticipant }) {
  const { user, trial, team } = useStore()
  const insightsFeature = team && utils.hasFeature(team, 'participantInsights') && trial?.participantInsightsEnabled && utils.canReadTrialInsights(user, trial)
  const packsFeature = team && utils.hasFeature(team, 'packs')
  const canListPacks = packsFeature && participant && utils.canListPacksForParticipant(user, trial, participant)
  const canAssignPacks = packsFeature && participant && utils.canAssignPacksForParticipant(user, trial, participant)
  const [loginLinkLoading, setLoginLinkLoading] = useState(false)
  const [loginLinkUrl, setLoginLinkUrl] = useState('')
  const [loginLinkOpen, setLoginLinkOpen] = useState(false)
  const [loginLinkExpiresAt, setLoginLinkExpiresAt] = useState(null)
  const [packTypes, setPackTypes] = useState([])
  const [assignedPacks, setAssignedPacks] = useState([])
  const [assignablePacks, setAssignablePacks] = useState([])
  const [packsLoading, setPacksLoading] = useState(false)
  const [assigningPackId, setAssigningPackId] = useState(null)

  useEffect(() => {
    if (!trialId || !participantId || !packsFeature) return
    setPacksLoading(true)
    api.packs.getPackTypesForTrial(trialId, (d) => {
      setPackTypes(d.packTypes || [])
    })
    api.participants.getPacks(trialId, participantId, (d) => {
      setAssignedPacks(d.packs || [])
      setPacksLoading(false)
    }, () => setPacksLoading(false))
  }, [trialId, participantId, packsFeature])

  useEffect(() => {
    if (!trialId || !participantId || !canAssignPacks) return
    api.participants.getAvailablePacks(trialId, participantId, (d) => {
      setAssignablePacks(d.packs || [])
    })
  }, [trialId, participantId, canAssignPacks])

  function packTypeName (pack) {
    const pt = packTypes.find((t) => String(t._id) === String(pack.packType))
    return pt?.name ?? ''
  }

  function assignPack (packId) {
    if (!packId) return
    setAssigningPackId(packId)
    api.participants.assignPack(trialId, participantId, packId, (d) => {
      setAssignedPacks((prev) => d.packs || [])
      setAssignablePacks((prev) => prev.filter((p) => String(p._id) !== String(packId)))
      setAssigningPackId(null)
      utils.toast('success', 'Pack assigned')
    }, (err) => {
      setAssigningPackId(null)
      utils.toast('error', err.message)
    })
  }

  function unassignPack (packId) {
    api.participants.unassignPack(trialId, participantId, packId, (d1) => {
      setAssignedPacks((prev) => prev.filter((p) => String(p._id) !== String(packId)))
      api.participants.getAvailablePacks(trialId, participantId, (d2) => {
        setAssignablePacks(d2.packs || [])
      })
      utils.toast('success', 'Pack unassigned')
    }, (err) => utils.toast('error', err.message))
  }

  function createLoginLink () {
    if (!trialId || !participantId) return
    setLoginLinkLoading(true)
    api.participants.createLoginLink(trialId, participantId, {}, (d) => {
      setLoginLinkUrl(d.url || '')
      setLoginLinkExpiresAt(d.expiresAt || null)
      setLoginLinkLoading(false)
    }, (err) => {
      setLoginLinkLoading(false)
      utils.toast('error', err.message)
    })
  }

  function openLoginLinkModal () {
    if (!trialId || !participantId) return
    setLoginLinkOpen(true)
  }

  function loadOrCreateLoginLink () {
    if (!trialId || !participantId) return
    setLoginLinkLoading(true)
    api.participants.getLoginLink(trialId, participantId, (d) => {
      if (d?.url) {
        setLoginLinkUrl(d.url || '')
        setLoginLinkExpiresAt(d.expiresAt || null)
        setLoginLinkLoading(false)
        return
      }
      api.participants.createLoginLink(trialId, participantId, {}, (d2) => {
        setLoginLinkUrl(d2.url || '')
        setLoginLinkExpiresAt(d2.expiresAt || null)
        setLoginLinkLoading(false)
      }, (err) => {
        setLoginLinkLoading(false)
        utils.toast('error', err.message)
      })
    }, (err) => {
      setLoginLinkLoading(false)
      utils.toast('error', err.message)
    })
  }

  useEffect(() => {
    if (!loginLinkOpen) return
    loadOrCreateLoginLink()
  }, [loginLinkOpen, trialId, participantId])

  if (!participant) {
    return (
      <Loading />
    )
  }

  return (
    <Stack gap={5}>
      {insightsFeature &&
        <InsightsBar trialId={trialId} participantId={participantId} />}

      <SimpleModal
        isOpen={loginLinkOpen}
        onClose={() => setLoginLinkOpen(false)}
        size='lg'
        header='Participant login link'
        body={
          <Stack gap={4}>
            <Box>
              <Text fontSize='sm' color='fg.subtle'>Share this link with the participant to let them sign in to the mobile app automatically. Once the app is downloaded, following this link (or scanning the QR code) will open the app and log them in.</Text>
            </Box>
            <Alert>Note that this login link will bypass eConsent verification at participant login.</Alert>
            <Text fontSize='xs' color='fg.muted'>Expires
              <Text ml={5} as='span' fontSize='sm' fontWeight='semibold'>
                {loginLinkExpiresAt ? utils.formatDateTime(loginLinkExpiresAt) : '—'}
              </Text>
            </Text>
            {loginLinkUrl && (
              <Stack gap={2} p={3} rounded='md' bg='bg.subtle'>
                <ReadOnlyCopyInput value={loginLinkUrl} placeholder={loginLinkLoading ? 'Loading login link…' : '—'} />
                <QrCode
                  name={`participant-login-${participant?.id || participantId}`}
                  url={loginLinkUrl}
                  size={300}
                  withDownload
                />
                <Box display='flex' justifyContent='end'>
                  <ConfirmButton
                    header='Re-generate login link?'
                    body='The existing link will be invalidated and will no longer work.'
                    trigger={<Button size='sm' variant='outline' loading={loginLinkLoading}>Re-generate link</Button>}
                    onConfirm={createLoginLink}
                  />
                </Box>
              </Stack>
            )}
          </Stack>
        }
        footer={
          <Stack justifyContent='end' direction='row'>
            <Button onClick={() => setLoginLinkOpen(false)}>Close</Button>
          </Stack>
        }
      />

      <ResponsiveStack gap={5}>
        <Stack minW='300px' borderWidth='1px' p={3} bg='bg.surface' rounded='md'>
          <Stack direction='row' justifyContent='end' gap={2}>
            {utils.canWriteParticipants(user, trial) &&
              <>
                {utils.canWriteTrial(user, trial) && utils.hasFeature(team, 'participantLoginLinks') &&
                  <Button size='sm' variant='outline' onClick={openLoginLinkModal}>Login link</Button>}
                <ParticipantEditor
                  trigger={<Button size='sm' variant='outline' colorPalette='primary' onClick={() => {}}>Edit details</Button>}
                  participant={participant}
                  onChange={newP => setParticipant(newP)}
                />
              </>}
          </Stack>

          <Stack gap={4}>
            {utils.canReadParticipantPersonalInformation(user, trial) && (participant.name || participant.dob || participant.email || participant.phone || participant.whatsapp) &&
              <Stack gap={0}>
                <Text fontSize='xs' color='fg.muted'>About this participant</Text>
                {participant.name && <Text fontSize='lg'>{participant.name}</Text>}
                {participant.dob && <Text fontSize='sm'>DOB: {utils.formatDate(participant.dob + 'T00:00:00')}</Text>}
                {participant.email && <Text fontSize='sm'>{participant.email}</Text>}
                {participant.phone && <Text fontSize='sm'>{participant.phone}</Text>}
                {participant.whatsapp && <Text fontSize='sm'>WhatsApp: {participant.whatsapp}</Text>}
              </Stack>}

            <Stack gap={0}>
              <Text fontSize='xs' color='fg.muted'>Timezone</Text>
              <Text fontSize='sm'>{participant.timezone || 'Europe/London'}</Text>
            </Stack>

            <Stack gap={0}>
              <Text fontSize='xs' color='fg.muted'>Status</Text>
              <Wrap direction='row'>
                {participant.isArchived && <WrapItem><Badge colorPalette='orange' variant='subtle'>Archived</Badge></WrapItem>}
                {participant.isWithdrawn && <WrapItem><Badge colorPalette='red' variant='subtle'>Withdrawn</Badge></WrapItem>}
                {participant.isUnallocated && <WrapItem><Badge colorPalette='yellow' variant='subtle'>Unallocated</Badge></WrapItem>}
                <WrapItem>
                  <Badge colorPalette={participant.passwordSet ? 'green' : 'gray'} variant='subtle'>
                    {participant.passwordSet ? 'Password set' : 'No password'}
                  </Badge>
                </WrapItem>
                {participant.pushAvailable &&
                  <WrapItem><Badge colorPalette='purple' variant='subtle'>Push</Badge></WrapItem>}
                {participant.emailAvailable && <WrapItem><Badge colorPalette='purple' variant='subtle'>Email</Badge></WrapItem>}
                {participant.smsAvailable && <WrapItem><Badge colorPalette='purple' variant='subtle'>SMS</Badge></WrapItem>}
                {participant.whatsappAvailable && <WrapItem><Badge colorPalette='purple' variant='subtle'>WhatsApp</Badge></WrapItem>}
              </Wrap>
            </Stack>

            <Stack gap={0}>
              <Text fontSize='xs' color='fg.muted'>Created</Text>
              <Text fontSize='sm'>{utils.formatDateTime(participant.createdAt)}</Text>
            </Stack>
            <Stack gap={0}>
              <Text fontSize='xs' color='fg.muted'>Last seen online</Text>
              <Text fontSize='sm'>{participant.lastSeenAt ? utils.formatDateTime(participant.lastSeenAt) : <Badge>Not seen yet</Badge>}</Text>
            </Stack>
            <Stack gap={0}>
              <Text fontSize='xs' color='fg.muted'>Groups</Text>
              <Text>{participant.groupObjects?.length > 0
                ? (
                  <Stack direction='row'>
                    {participant.groupObjects.map(g =>
                      <ColouredTag size='sm' key={g._id} colour={g.colour} label={g.name} />
                    )}
                  </Stack>)
                : <Badge>No groups</Badge>}
              </Text>
            </Stack>
            <Stack gap={0}>
              <Text fontSize='xs' color='fg.muted'>Trial sites</Text>
              <Text>{participant.siteObjects?.length > 0
                ? (
                  <Stack direction='row'>
                    {participant.siteObjects.map(s =>
                      <Tag size='sm' key={s._id} variant='subtle' label={s.name} />
                    )}
                  </Stack>)
                : <Badge>No sites</Badge>}
              </Text>
            </Stack>

            {canListPacks && packsFeature && (
              <Stack gap={4} p={3} rounded='md' bg='bg.subtle'>
                <Heading size='sm'>Packs</Heading>
                {packsLoading
                  ? <Text fontSize='sm'>Loading…</Text>
                  : (
                    <>
                      {assignedPacks.length > 0 && (
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                          {assignedPacks.map((pack) => (
                            <Box
                              key={pack._id}
                              rounded='md'
                              shadow='sm'
                              bg='bg.surface'
                              p={3}
                            >
                              <Stack gap={2}>
                                <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                                  <Stack direction='row' alignItems='center' gap={2}>
                                    <Icon as={FaBox} fontSize='md' color='fg.subtle' />
                                    <Text fontWeight='bold' fontSize='sm'>{pack.name ?? '—'}</Text>
                                  </Stack>
                                  {canAssignPacks && (
                                    <ConfirmButton
                                      trigger={<Button size='xs' variant='ghost' aria-label='Unassign pack'><Icon as={FaTimes} /></Button>}
                                      header='Unassign this pack from the participant?'
                                      onConfirm={() => unassignPack(pack._id)}
                                    />
                                  )}
                                </Stack>
                                <Text fontFamily='mono' fontSize='xs' color='fg.muted'>{pack.batchNumber ?? '—'}</Text>
                                <Box>
                                  <Badge fontSize='xs' color='fg.muted'>{packTypeName(pack) || '—'}</Badge>
                                </Box>
                              </Stack>
                            </Box>
                          ))}
                        </SimpleGrid>
                      )}
                      {canAssignPacks && (
                        <Box>
                          <SearchableSelect
                            size='sm' w='100%'
                            placeholder='Assign a pack…'
                            options={assignablePacks
                              .filter((p) => String(p.participant || '') !== String(participantId))
                              .map((p) => ({
                                value: p._id,
                                label: [p.name, p.batchNumber, packTypeName(p)].filter(Boolean).join(' · ') || p._id
                              }))}
                            value=''
                            onChange={(val) => { assignPack(val) }}
                            emptyMessage='No assignable packs'
                          />
                          {assigningPackId && <Text fontSize='xs' mt={1}>Assigning…</Text>}
                        </Box>
                      )}
                      {!packsLoading && assignedPacks.length === 0 && !canAssignPacks && <Text fontSize='sm'>No packs assigned.</Text>}
                    </>
                    )}
              </Stack>
            )}

            {participant.connectedServices?.length > 0 &&
              <Stack>
                <Heading size='sm'>Connected services</Heading>
                {participant.connectedServices.filter(s => SERVICES[s.key]).map(s =>
                  <Stack gap={4} key={s.key} direction='row' alignItems='center'>
                    <ImageLogo w='80px' src={SERVICES[s.key]?.image} alt={SERVICES[s.key]?.name} />
                    <Text fontSize='sm'>{SERVICES[s.key]?.name} connected on {utils.formatDateTime(s.connectedAt)}</Text>
                  </Stack>
                )}
              </Stack>}
          </Stack>
        </Stack>

        <Box flex={1}>
          <ParticipantNotes participant={participant} />
        </Box>
      </ResponsiveStack>

      <ResponsiveStack gap={5}>
        <Box rounded='md' p={3} bg='bg.surface' borderWidth='1px'>
          <Box>
            <Heading size='md'>Messages</Heading>
            <Text fontSize='sm'>Messages sent and received with this participant.</Text>
          </Box>
          {utils.canSupportTrial(user, trial) &&
            <ParticipantMessenger trial={trial} participant={participant} />}
        </Box>

        <Stack gap={4} flex={1} bg='bg.surface' rounded='md' p={3} borderWidth='1px'>
          <Box>
            <Heading size='md'>Recent activity</Heading>
            <Text fontSize='sm'>Actions recently taken by the participant.</Text>
          </Box>
          {!participant?.recentLogs?.length > 0 &&
            <EmptyBox icon='logs' title='Activity feed' description='Entries will appear here once the participant engages with the trial.' />}
          <Stack overflowY='auto' maxH='500px'>
            {participant?.recentLogs?.map(log =>
              <Stack direction='row' key={log._id}>
                <Text fontSize='xs' mr={2} w='120px'>{utils.formatDateTime(log.createdAt)}</Text>
                <Text fontSize='xs' fontWeight='bold'>{log.type}</Text>
              </Stack>
            )}
          </Stack>
        </Stack>
      </ResponsiveStack>
    </Stack>
  )
}

function ParticipantEditor ({ trigger, participant, onChange }) {
  const { user, trial, anchors } = useStore()
  const canReadPii = utils.canReadParticipantPersonalInformation(user, trial)
  const [isOpen, setIsOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [participantCode, setParticipantCode] = useState('')
  const [name, setName] = useState('')
  const [dob, setDob] = useState(null)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [timezone, setTimezone] = useState('Europe/London')
  // Anchor state: local copy of participant.anchors (anchorId -> ISO string or null)
  const [anchorValues, setAnchorValues] = useState({})

  const timezoneOptions = useMemo(() =>
    Intl.supportedValuesOf('timeZone').map(tz => ({ value: tz, label: tz }))
  , [])

  useEffect(() => {
    if (!participant) return
    setPassword('')
    setParticipantCode(participant.id || '')
    setName(participant.name || '')
    setDob(participant.dob ? new Date(participant.dob + 'T00:00:00') : null)
    setEmail(participant.email || '')
    setPhone(participant.phone || '')
    setWhatsapp(participant.whatsapp || '')
    setTimezone(participant.timezone || 'Europe/London')
    const parsed = {}
    for (const [k, v] of Object.entries(participant.anchors || {})) {
      parsed[k] = v ? new Date(v) : null
    }
    setAnchorValues(parsed)
  }, [participant])

  const savePassword = () => {
    api.trials.updateParticipant(participant.trial, participant._id, { password }, data => {
      onChange({ ...participant, passwordSet: data.passwordSet })
      setIsOpen(false)
    }, err => {
      utils.toast('error', 'Unable to save password', err.message)
    })
  }

  const saveParticipantId = () => {
    const code = (participantCode || '').trim()
    if (!code) {
      utils.toast('error', 'Participant ID is required')
      return
    }
    api.trials.updateParticipant(participant.trial, participant._id, { id: code }, d => {
      onChange({ ...participant, ...d })
      setIsOpen(false)
    }, err => {
      utils.toast('error', 'Unable to save participant', err.message)
    })
  }

  const savePersonalDetails = () => {
    if (!canReadPii) return
    const data = {
      name: name || null,
      dob: dob ? `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}` : null,
      email: email || null,
      phone: phone || null,
      whatsapp: whatsapp || null
    }
    api.trials.updateParticipant(participant.trial, participant._id, data, d => {
      onChange({ ...participant, ...d })
      setIsOpen(false)
    }, err => {
      utils.toast('error', 'Unable to save participant', err.message)
    })
  }

  const saveTimezone = () => {
    api.trials.updateParticipant(participant.trial, participant._id, { timezone: timezone || null }, d => {
      onChange({ ...participant, ...d })
      setIsOpen(false)
    }, err => {
      utils.toast('error', 'Unable to save participant', err.message)
    })
  }

  const saveAnchor = (anchorId) => {
    const date = anchorValues?.[anchorId]
    if (!date) return
    // Use midnight UTC of the local calendar date the admin chose, regardless of their timezone.
    // This mirrors the calendar drag/confirm approach and ensures _apply_enable_time on the
    // server sees the correct local date for the participant.
    const isoString = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString()
    api.anchors.setParticipantAnchor(participant.trial, participant._id, anchorId, isoString, d => {
      onChange({ ...participant, anchors: d.anchors || {} })
      utils.toast('success', 'Anchor date saved')
    }, err => utils.toast('error', 'Unable to save anchor date', err.message))
  }

  const clearAnchor = (anchorId) => {
    api.anchors.clearParticipantAnchor(participant.trial, participant._id, anchorId, d => {
      setAnchorValues(prev => ({ ...prev, [anchorId]: null }))
      onChange({ ...participant, anchors: d.anchors || {} })
      utils.toast('success', 'Anchor date cleared')
    }, err => utils.toast('error', 'Unable to clear anchor date', err.message))
  }

  if (!participant) return null
  return (
    <>
      {React.cloneElement(trigger, { onClick: () => setIsOpen(true) })}
      <SimpleModal
        isOpen={isOpen} onClose={() => setIsOpen(false)}
        modalProps={{ size: 'lg' }}
        header={`Edit details for participant ${participantCode || participant.id}`}
        body={
          <Stack gap={5}>
            <Stack gap={3} p={3} rounded='md' bg='bg.surface' borderWidth='1px'>
              <Box>
                <Heading size='md'>Participant ID</Heading>
                <Text fontSize='sm' color='fg.subtle'>The primary identifier for this participant.</Text>
              </Box>
              <FormControl label='Participant ID'>
                <Input
                  bg='bg.surface'
                  placeholder='e.g. P001'
                  type='text'
                  value={participantCode || ''}
                  onChange={e => setParticipantCode(e.target.value)}
                />
              </FormControl>
              <Box display='flex' justifyContent='end'>
                <Button colorPalette='primary' onClick={saveParticipantId}>Save participant ID</Button>
              </Box>
            </Stack>

            {canReadPii &&
              <Stack gap={3} p={3} rounded='md' bg='bg.surface' borderWidth='1px'>
                <Box>
                  <Heading size='md'>Personal details</Heading>
                  <Text fontSize='sm' color='fg.subtle'>The personal details of the participant. Trialflare will use the contact details below to send reminders and notifications.</Text>
                </Box>

                <FormControl label='Full name'>
                  <Input bg='bg.surface' placeholder='Jane Smith' type='text' value={name || ''} onChange={e => setName(e.target.value)} />
                </FormControl>

                <FormControl label='Date of birth'>
                  <DateChooser value={dob} onChange={setDob} />
                </FormControl>

                <FormControl label='Email address'>
                  <Input bg='bg.surface' placeholder='jane@example.com' type='email' value={email || ''} onChange={e => setEmail(e.target.value)} />
                </FormControl>

                <FormControl label='Phone (SMS) number'>
                  <PhoneInput value={phone} onChange={setPhone} />
                </FormControl>

                <FormControl label='WhatsApp number'>
                  <PhoneInput value={whatsapp} onChange={setWhatsapp} isWhatsapp />
                </FormControl>

                <Box display='flex' justifyContent='end'>
                  <Button colorPalette='primary' onClick={savePersonalDetails}>Save details</Button>
                </Box>
              </Stack>}

            <Stack gap={3} p={3} rounded='md' bg='bg.surface' borderWidth='1px'>
              <Box>
                <Heading size='md'>Timezone</Heading>
                <Text fontSize='sm' color='fg.subtle'>Used to send reminders and notifications at the correct local time for this participant.</Text>
              </Box>
              <FormControl label='Timezone'>
                <SearchableSelect
                  options={timezoneOptions}
                  value={timezone}
                  onChange={v => setTimezone(v)}
                />
              </FormControl>
              <Box display='flex' justifyContent='end'>
                <Button colorPalette='primary' onClick={saveTimezone}>Save timezone</Button>
              </Box>
            </Stack>

            <Stack gap={3} p={3} rounded='md' bg='bg.surface' borderWidth='1px'>
              <Box>
                <Heading size='md'>Login password</Heading>
                <Text fontSize='sm' color='fg.subtle'>The password used to login to the trial. If not set, the trial password is used instead.</Text>
              </Box>
              {participant.passwordSet &&
                <Badge colorPalette='green' variant='subtle'>A password is currently set</Badge>}

              <FormControl label='Password' helperText='Save whilst blank to remove the password'>
                <Input bg='bg.surface' placeholder='Type a new password...' type='password' value={password || ''} onChange={e => setPassword(e.target.value)} />
              </FormControl>
              <Box display='flex' justifyContent='end'>
                <Button colorPalette='primary' onClick={savePassword}>{password?.length ? 'Save password' : 'Set no password'}</Button>
              </Box>
            </Stack>

            {anchors?.length > 0 && (
              <Stack gap={3} p={3} rounded='md' bg='bg.surface' borderWidth='1px'>
                <Box>
                  <Heading size='md'>Anchor dates</Heading>
                  <Text fontSize='sm' color='fg.subtle'>Anchor dates are used to schedule stages relative to participant-specific events.</Text>
                </Box>
                {anchors.map(anchor => (
                  <Stack key={anchor._id} gap={1}>
                    <Text fontSize='sm' fontWeight='semibold'>{anchor.name}</Text>
                    {anchor.description && <Text fontSize='xs' color='fg.muted'>{anchor.description}</Text>}
                    <Stack direction='row' gap={2} alignItems='center'>
                      <DateChooser
                        value={anchorValues?.[anchor._id] || null}
                        onChange={date => setAnchorValues(prev => ({ ...prev, [anchor._id]: date }))}
                      />
                      <Button
                        size='sm' colorPalette='primary' variant='outline'
                        isDisabled={!anchorValues?.[anchor._id]}
                        onClick={() => saveAnchor(anchor._id)}
                      >Save
                      </Button>
                      <Button
                        size='sm' variant='ghost' colorPalette='red'
                        isDisabled={!anchorValues?.[anchor._id]}
                        onClick={() => clearAnchor(anchor._id)}
                      >Clear
                      </Button>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        }
        footer={
          <Stack justifyContent='end' direction='row'>
            <Button onClick={() => setIsOpen(false)}>Close</Button>
          </Stack>
        }
      />
    </>
  )
}
